import React, { useState, useEffect, useCallback } from 'react';
import { jobCostingAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Loader2, ArrowLeft, Archive, RotateCcw, Pencil, Check, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Section: Units
// ---------------------------------------------------------------------------
function UnitsSection({ canEdit }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAbbr, setNewAbbr] = useState('');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAbbr, setEditAbbr] = useState('');

  const fetch = useCallback(async () => {
    try {
      const res = await jobCostingAPI.listUnits({ include_archived: showArchived });
      setUnits(res.data.units || []);
    } catch { toast.error('Failed to load units'); }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await jobCostingAPI.createUnit({ name: newName.trim(), abbreviation: newAbbr.trim() || null });
      setNewName(''); setNewAbbr(''); setAdding(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add unit');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await jobCostingAPI.updateUnit(id, { name: editName.trim(), abbreviation: editAbbr.trim() || null });
      setEditId(null); fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const toggleArchive = async (unit) => {
    try {
      await jobCostingAPI.updateUnit(unit.id, { is_archived: !unit.is_archived });
      toast.success(unit.is_archived ? 'Unit restored' : 'Unit archived');
      fetch();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="units-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Units of Measure</h3>
          <span className="text-xs text-muted-foreground">{units.filter(u => !u.is_archived).length} active</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowArchived(!showArchived)} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-archived-units">
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          {canEdit && !adding && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-unit-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          )}
        </div>
      </div>

      <div className="border rounded-lg divide-y">
        {/* Header */}
        <div className="grid grid-cols-[1fr_100px_80px] px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <span>Name</span>
          <span>Abbreviation</span>
          <span className="text-right">Actions</span>
        </div>
        {units.map(u => (
          <div key={u.id} className={`grid grid-cols-[1fr_100px_80px] px-4 py-2.5 items-center text-sm group ${u.is_archived ? 'opacity-50' : ''}`} data-testid={`unit-row-${u.id}`}>
            {editId === u.id ? (
              <>
                <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-7 text-sm" autoFocus data-testid="edit-unit-name" />
                <Input value={editAbbr} onChange={e => setEditAbbr(e.target.value)} className="h-7 text-sm" data-testid="edit-unit-abbr" />
                <div className="flex justify-end gap-1">
                  <button onClick={() => handleUpdate(u.id)} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded" data-testid="save-edit-unit"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditId(null)} className="p-1 text-muted-foreground hover:bg-muted rounded"><X className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{u.name}</span>
                  {u.is_archived && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Archived</Badge>}
                </div>
                <span className="text-muted-foreground">{u.abbreviation || '—'}</span>
                <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canEdit && !u.is_archived && (
                    <button onClick={() => { setEditId(u.id); setEditName(u.name); setEditAbbr(u.abbreviation || ''); }} className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded" data-testid={`edit-unit-${u.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  )}
                  {canEdit && (
                    <button onClick={() => toggleArchive(u)} className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded" title={u.is_archived ? 'Restore' : 'Archive'} data-testid={`archive-unit-${u.id}`}>
                      {u.is_archived ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        {/* Add row */}
        {adding && (
          <div className="grid grid-cols-[1fr_100px_80px] px-4 py-2.5 items-center bg-blue-50/50 dark:bg-blue-950/20" data-testid="add-unit-row">
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Unit name" className="h-7 text-sm" autoFocus data-testid="new-unit-name" />
            <Input value={newAbbr} onChange={e => setNewAbbr(e.target.value)} placeholder="e.g. ea" className="h-7 text-sm" data-testid="new-unit-abbr" />
            <div className="flex justify-end gap-1">
              <button onClick={handleAdd} disabled={saving || !newName.trim()} className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-40" data-testid="confirm-add-unit"><Check className="w-4 h-4" /></button>
              <button onClick={() => { setAdding(false); setNewName(''); setNewAbbr(''); }} className="p-1 text-muted-foreground hover:bg-muted rounded"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {units.length === 0 && !adding && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">No units configured yet.</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Cost Types
// ---------------------------------------------------------------------------
function CostTypesSection({ canEdit }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', default_margin: '30', default_markup: '42.86', is_time_trackable: false, is_taxable: false });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetch = useCallback(async () => {
    try {
      const res = await jobCostingAPI.listCostTypes({ include_archived: showArchived });
      setTypes(res.data.cost_types || []);
    } catch { toast.error('Failed to load cost types'); }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!newForm.name.trim()) return;
    setSaving(true);
    try {
      await jobCostingAPI.createCostType({
        name: newForm.name.trim(),
        default_margin: parseFloat(newForm.default_margin) || null,
        default_markup: parseFloat(newForm.default_markup) || null,
        is_time_trackable: newForm.is_time_trackable,
        is_taxable: newForm.is_taxable,
      });
      setNewForm({ name: '', default_margin: '30', default_markup: '42.86', is_time_trackable: false, is_taxable: false });
      setAdding(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await jobCostingAPI.updateCostType(id, {
        name: editForm.name?.trim(),
        default_margin: editForm.default_margin !== '' ? parseFloat(editForm.default_margin) : null,
        default_markup: editForm.default_markup !== '' ? parseFloat(editForm.default_markup) : null,
        is_time_trackable: editForm.is_time_trackable,
        is_taxable: editForm.is_taxable,
      });
      setEditId(null); fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const toggleArchive = async (ct) => {
    try {
      await jobCostingAPI.updateCostType(ct.id, { is_archived: !ct.is_archived });
      toast.success(ct.is_archived ? 'Cost type restored' : 'Cost type archived');
      fetch();
    } catch { toast.error('Failed'); }
  };

  const startEdit = (ct) => {
    setEditId(ct.id);
    setEditForm({
      name: ct.name,
      default_margin: ct.default_margin ?? '',
      default_markup: ct.default_markup ?? '',
      is_time_trackable: ct.is_time_trackable,
      is_taxable: ct.is_taxable,
    });
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="cost-types-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Cost Types</h3>
          <span className="text-xs text-muted-foreground">{types.filter(t => !t.is_archived).length} active</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowArchived(!showArchived)} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-archived-types">
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          {canEdit && !adding && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-cost-type-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          )}
        </div>
      </div>

      <div className="border rounded-lg divide-y">
        <div className="grid grid-cols-[1fr_90px_90px_90px_90px_80px] px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <span>Name</span>
          <span className="text-center">Margin %</span>
          <span className="text-center">Markup %</span>
          <span className="text-center">Trackable</span>
          <span className="text-center">Taxable</span>
          <span className="text-right">Actions</span>
        </div>
        {types.map(ct => (
          <div key={ct.id} className={`grid grid-cols-[1fr_90px_90px_90px_90px_80px] px-4 py-2.5 items-center text-sm group ${ct.is_archived ? 'opacity-50' : ''}`} data-testid={`cost-type-row-${ct.id}`}>
            {editId === ct.id ? (
              <>
                <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="h-7 text-sm" autoFocus data-testid="edit-ct-name" />
                <Input type="number" value={editForm.default_margin} onChange={e => setEditForm(f => ({ ...f, default_margin: e.target.value }))} className="h-7 text-sm text-center" data-testid="edit-ct-margin" />
                <Input type="number" value={editForm.default_markup} onChange={e => setEditForm(f => ({ ...f, default_markup: e.target.value }))} className="h-7 text-sm text-center" data-testid="edit-ct-markup" />
                <div className="flex justify-center"><Switch checked={editForm.is_time_trackable} onCheckedChange={v => setEditForm(f => ({ ...f, is_time_trackable: v }))} data-testid="edit-ct-trackable" /></div>
                <div className="flex justify-center"><Switch checked={editForm.is_taxable} onCheckedChange={v => setEditForm(f => ({ ...f, is_taxable: v }))} data-testid="edit-ct-taxable" /></div>
                <div className="flex justify-end gap-1">
                  <button onClick={() => handleUpdate(ct.id)} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded" data-testid="save-edit-ct"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditId(null)} className="p-1 text-muted-foreground hover:bg-muted rounded"><X className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{ct.name}</span>
                  {ct.is_archived && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Archived</Badge>}
                </div>
                <span className="text-center tabular-nums text-muted-foreground">{ct.default_margin != null ? `${ct.default_margin}%` : '—'}</span>
                <span className="text-center tabular-nums text-muted-foreground">{ct.default_markup != null ? `${ct.default_markup}%` : '—'}</span>
                <div className="flex justify-center">
                  <div className={`w-2 h-2 rounded-full ${ct.is_time_trackable ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`} title={ct.is_time_trackable ? 'Time trackable' : 'Not time trackable'} />
                </div>
                <div className="flex justify-center">
                  <div className={`w-2 h-2 rounded-full ${ct.is_taxable ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`} title={ct.is_taxable ? 'Taxable' : 'Not taxable'} />
                </div>
                <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canEdit && !ct.is_archived && (
                    <button onClick={() => startEdit(ct)} className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded" data-testid={`edit-ct-${ct.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  )}
                  {canEdit && (
                    <button onClick={() => toggleArchive(ct)} className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded" title={ct.is_archived ? 'Restore' : 'Archive'} data-testid={`archive-ct-${ct.id}`}>
                      {ct.is_archived ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        {/* Add row */}
        {adding && (
          <div className="grid grid-cols-[1fr_90px_90px_90px_90px_80px] px-4 py-2.5 items-center bg-blue-50/50 dark:bg-blue-950/20" data-testid="add-cost-type-row">
            <Input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="Cost type name" className="h-7 text-sm" autoFocus data-testid="new-ct-name" />
            <Input type="number" value={newForm.default_margin} onChange={e => setNewForm(f => ({ ...f, default_margin: e.target.value }))} className="h-7 text-sm text-center" data-testid="new-ct-margin" />
            <Input type="number" value={newForm.default_markup} onChange={e => setNewForm(f => ({ ...f, default_markup: e.target.value }))} className="h-7 text-sm text-center" data-testid="new-ct-markup" />
            <div className="flex justify-center"><Switch checked={newForm.is_time_trackable} onCheckedChange={v => setNewForm(f => ({ ...f, is_time_trackable: v }))} data-testid="new-ct-trackable" /></div>
            <div className="flex justify-center"><Switch checked={newForm.is_taxable} onCheckedChange={v => setNewForm(f => ({ ...f, is_taxable: v }))} data-testid="new-ct-taxable" /></div>
            <div className="flex justify-end gap-1">
              <button onClick={handleAdd} disabled={saving || !newForm.name.trim()} className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-40" data-testid="confirm-add-ct"><Check className="w-4 h-4" /></button>
              <button onClick={() => setAdding(false)} className="p-1 text-muted-foreground hover:bg-muted rounded"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {types.length === 0 && !adding && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">No cost types configured yet.</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Cost Codes (hierarchical)
// ---------------------------------------------------------------------------
function CostCodesSection({ canEdit }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState({ code: '', name: '', parent_id: '' });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetch = useCallback(async () => {
    try {
      const res = await jobCostingAPI.listCostCodes({ include_archived: showArchived });
      setCodes(res.data.cost_codes || []);
    } catch { toast.error('Failed to load cost codes'); }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { fetch(); }, [fetch]);

  // Top-level codes for parent dropdown
  const parentOptions = codes.filter(c => !c.parent_id && !c.is_archived);

  // Group codes: parents first, then their children
  const grouped = [];
  const topLevel = codes.filter(c => !c.parent_id);
  for (const parent of topLevel) {
    grouped.push(parent);
    const children = codes.filter(c => c.parent_id === parent.id);
    children.sort((a, b) => a.code.localeCompare(b.code));
    grouped.push(...children);
  }
  // Add orphan children (parent might be archived)
  const addedIds = new Set(grouped.map(g => g.id));
  for (const c of codes) {
    if (!addedIds.has(c.id)) grouped.push(c);
  }

  const handleAdd = async () => {
    if (!newForm.code.trim() || !newForm.name.trim()) return;
    setSaving(true);
    try {
      await jobCostingAPI.createCostCode({
        code: newForm.code.trim(),
        name: newForm.name.trim(),
        parent_id: newForm.parent_id || null,
      });
      setNewForm({ code: '', name: '', parent_id: '' });
      setAdding(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await jobCostingAPI.updateCostCode(id, {
        code: editForm.code?.trim(),
        name: editForm.name?.trim(),
        parent_id: editForm.parent_id || null,
      });
      setEditId(null); fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const toggleArchive = async (cc) => {
    try {
      await jobCostingAPI.updateCostCode(cc.id, { is_archived: !cc.is_archived });
      toast.success(cc.is_archived ? 'Cost code restored' : 'Cost code archived');
      fetch();
    } catch { toast.error('Failed'); }
  };

  const startEdit = (cc) => {
    setEditId(cc.id);
    setEditForm({ code: cc.code, name: cc.name, parent_id: cc.parent_id || '' });
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="cost-codes-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Cost Codes</h3>
          <span className="text-xs text-muted-foreground">{codes.filter(c => !c.is_archived).length} active</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowArchived(!showArchived)} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-archived-codes">
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          {canEdit && !adding && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-cost-code-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          )}
        </div>
      </div>

      <div className="border rounded-lg divide-y">
        <div className="grid grid-cols-[80px_1fr_180px_80px] px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <span>Code</span>
          <span>Name</span>
          <span>Parent</span>
          <span className="text-right">Actions</span>
        </div>
        {grouped.map(cc => {
          const isChild = !!cc.parent_id;
          return (
            <div key={cc.id} className={`grid grid-cols-[80px_1fr_180px_80px] px-4 py-2.5 items-center text-sm group ${cc.is_archived ? 'opacity-50' : ''} ${isChild ? 'bg-muted/10' : ''}`} data-testid={`cost-code-row-${cc.id}`}>
              {editId === cc.id ? (
                <>
                  <Input value={editForm.code} onChange={e => setEditForm(f => ({ ...f, code: e.target.value }))} className="h-7 text-sm w-16" autoFocus data-testid="edit-cc-code" />
                  <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="h-7 text-sm" data-testid="edit-cc-name" />
                  <Select value={editForm.parent_id || 'none'} onValueChange={v => setEditForm(f => ({ ...f, parent_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="h-7 text-sm" data-testid="edit-cc-parent"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No parent</SelectItem>
                      {parentOptions.filter(p => p.id !== cc.id).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.code} {p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex justify-end gap-1">
                    <button onClick={() => handleUpdate(cc.id)} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded" data-testid="save-edit-cc"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditId(null)} className="p-1 text-muted-foreground hover:bg-muted rounded"><X className="w-4 h-4" /></button>
                  </div>
                </>
              ) : (
                <>
                  <span className={`font-mono tabular-nums ${isChild ? 'pl-4 text-muted-foreground' : 'font-semibold'}`}>
                    {isChild && <ChevronRight className="w-3 h-3 inline mr-0.5 -mt-0.5 text-muted-foreground/50" />}
                    {cc.code}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={isChild ? 'text-muted-foreground' : 'font-medium'}>{cc.name}</span>
                    {cc.is_archived && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Archived</Badge>}
                  </div>
                  <span className="text-muted-foreground text-xs">{cc.parent_name || '—'}</span>
                  <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEdit && !cc.is_archived && (
                      <button onClick={() => startEdit(cc)} className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded" data-testid={`edit-cc-${cc.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    )}
                    {canEdit && (
                      <button onClick={() => toggleArchive(cc)} className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded" title={cc.is_archived ? 'Restore' : 'Archive'} data-testid={`archive-cc-${cc.id}`}>
                        {cc.is_archived ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
        {/* Add row */}
        {adding && (
          <div className="grid grid-cols-[80px_1fr_180px_80px] px-4 py-2.5 items-center bg-blue-50/50 dark:bg-blue-950/20" data-testid="add-cost-code-row">
            <Input value={newForm.code} onChange={e => setNewForm(f => ({ ...f, code: e.target.value }))} placeholder="Code" className="h-7 text-sm w-16" autoFocus data-testid="new-cc-code" />
            <Input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="Cost code name" className="h-7 text-sm" data-testid="new-cc-name" />
            <Select value={newForm.parent_id || 'none'} onValueChange={v => setNewForm(f => ({ ...f, parent_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="h-7 text-sm" data-testid="new-cc-parent"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No parent (top-level)</SelectItem>
                {parentOptions.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.code} {p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-1">
              <button onClick={handleAdd} disabled={saving || !newForm.code.trim() || !newForm.name.trim()} className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-40" data-testid="confirm-add-cc"><Check className="w-4 h-4" /></button>
              <button onClick={() => setAdding(false)} className="p-1 text-muted-foreground hover:bg-muted rounded"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {codes.length === 0 && !adding && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">No cost codes configured yet.</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function JobCostingSettings() {
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  return (
    <Layout>
      <div className="space-y-6" data-testid="job-costing-settings-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Job Costing</h1>
            <p className="text-sm text-muted-foreground">Configure units, cost types, and cost codes for standardized budgeting and estimating.</p>
          </div>
        </div>

        {/* Three stacked sections */}
        <div className="space-y-8">
          <UnitsSection canEdit={canEdit} />
          <CostTypesSection canEdit={canEdit} />
          <CostCodesSection canEdit={canEdit} />
        </div>
      </div>
    </Layout>
  );
}
